<template>
  <div class="playerContainer" :class="{ loaded: playerLoaded }">
    <template v-if="!isKslLivestream || (isKslLivestream && !adPlayed)">
      <div :id="id">
        <span class="loadingMedia">Loading media...</span>
      </div>
      <template v-if="playerLoaded">
        <v-btn v-if="showSkipButton" @click.native="skipAd(); $gtag.clickButton('Skip Ad')" class="customControl skipAdButton" dark>
          Skip Ad
        </v-btn>
        <span class="skipAdText" v-if="showSkipText">
          Skip in {{ timeToSkip }} seconds
        </span>
        <v-btn
          v-if="showCustomControls"
          @click="toggleMute"
          class="customControl muteButton"
          dark
        >
          <v-icon>mdi-{{ muted ? 'volume-off' : 'volume-high' }}</v-icon>
        </v-btn>
        <v-btn
          v-if="showCustomControls"
          @click="togglePlay"
          class="customControl playButton"
          dark
        >
          <v-icon>mdi-{{ playing ? 'pause' : 'play' }}</v-icon>
        </v-btn>
      </template>
    </template>
    <div
      id="kslPlayerContainer"
      :class="{ adPlayed: adPlayed }"
      v-if="isKslLivestream"
    >
      <iframe
        v-show="adPlayed"
        frameborder="0"
        :src="broadcast.player_url"
        allowfullscreen="allowfullscreen"
        mozallowfullscreen="mozallowfullscreen" 
        msallowfullscreen="msallowfullscreen" 
        oallowfullscreen="oallowfullscreen" 
        webkitallowfullscreen="webkitallowfullscreen"
      ></iframe>
    </div>
  </div>
</template>
<script>
export default {
  props: {
    videoAd: {
      type: Object,
      required: false,
    },
    broadcast: {
      type: Object,
      required: true,
    },
    hideLogoUrl: {
      type: Boolean,
      required: false,
      default: false,
    },
    logoLink: {
      type: String,
      required: false,
      default: 'https://teamhive.live/',
    },
  },
  data: () => ({
    player: null,
    playerLoaded: false,
    keys: [],
    adPlayed: false,
    position: 0,
    skipThreshold: 20,
    muted: false,
    playing: true,
    logoUrl: `${process.env.BASE_URL}img/teamhive_logo_sm_60_opacity.png`
  }),
  computed: {
    playerConfig() {
      return {
        nextUpDisplay: false,
        base: `${process.env.BASE_URL}jwplayer/`,
        client: `${process.env.BASE_URL}jwplayer/jwpsrv.js`,
        cast: {},
        sharing: {},
        controls: this.adPlayed,
        playlist: [
          {
            file: this.videoAd ? this.videoAd.file : null,
          },
        ],
        logo: {
          file: this.hideLogoUrl ? null : this.logoUrl,
          link: this.logoLink,
        },
        width: '100%',
        aspectratio: '16:9',
        autostart: 'viewable',
        abouttext: 'Service Provided By CentraCom',
        aboutlink: 'https://centracom.com',
      };
    },
    id() {
      let idNum = this.broadcast.id * 1000;
      return idNum.toString(16);
    },
    showSkipButton() {
      if (!this.adPlayed && this.position >= this.skipThreshold) {
        return true;
      }
      return false;
    },
    showSkipText() {
      if (!this.adPlayed && this.position < this.skipThreshold) {
        return true;
      }
      return false;
    },
    timeToSkip() {
      return Math.ceil(this.skipThreshold - this.position);
    },
    showCustomControls() {
      if (!this.adPlayed) {
        return true;
      }
      return false;
    },
    isKslLivestream() {
      let url = this.broadcast.player_url;
      if (url.match(/ksl\.com/)) {
        return true;
      }
      return false;
    }
  },
  methods: {
    loadPlayerScript() {
      if (!document.getElementById('jwplayerScript')) {
        const script = document.createElement('script');
        script.onload = function() {
          if (window.jwplayer) {
            window.jwplayer.key =
              'dQLO+JcWyiGkoYe6J/+h1GDqondcxo33eBpbR1DPM8U=';
            this.jwplayerSetup();
          }
        }.bind(this);
        script.src = `${process.env.BASE_URL}jwplayer/jwplayer.js`;
        script.id = 'jwplayerScript';
        document.head.appendChild(script);
      } else {
        this.jwplayerSetup();
      }
    },
    jwplayerSetup() {
      if (window.jwplayer) {
        window.localStorage.setItem('jwplayer.mute', false);
        this.player = window.jwplayer(this.id);
        if (this.videoAd) {
          this.player.setup(this.playerConfig);
          this.playerLoaded = true;
          this.addListeners();
        } else {
          this.loadBroadcast();
        }
        this.addKeyEventListeners();
      }
    },
    loadBroadcast() {
      if (!this.adPlayed) {
        this.adPlayed = true;
        let postAdConfig = { ...this.playerConfig };
        postAdConfig.playlist = [
          {
            file: this.broadcast.player_url,
          },
        ];
        this.player.setup(postAdConfig);
        this.playerLoaded = true;
        this.addListeners();
      }
    },
    reconnect() {
      this.adPlayed = true;
      let postAdConfig = { ...this.playerConfig };
      postAdConfig.playlist = [
        {
          file: this.broadcast.player_url,
        },
      ];
      this.player.setup(postAdConfig);
      this.playerLoaded = true;
      this.addListeners();
    },
    addListeners() {
      this.player.on('ready', () => {
        // Safari is doing some weird stuff if we don't manually get the muted status.
        setTimeout(() => {
          this.muted = this.player.getMute();
        }, 200)
      });
      this.player.on('time', e => {
        this.position = e.position;
      });
      this.player.on('displayClick', () => {
        if (!this.adPlayed) {
          this.openSponsorLink();
          return;
        }
      });
      this.player.on('error', e => {
        console.log('Player Error', e);
        this.$emit('error', e);
      });
      this.player.on('playlistComplete', () => {
        this.loadBroadcast();
      });
    },
    skipAd() {
      if (this.position >= this.skipThreshold) {
        this.loadBroadcast();
      }
      return false;
    },
    toggleMute() {
      if (this.player) {
        if (this.playerMuted()) {
          this.player.setMute(false);
          this.muted = false;
        } else {
          this.player.setMute(true);
          this.muted = true;
        }
      }
    },
    togglePlay() {
      if (this.player) {
        if (this.playerPaused()) {
          this.player.play();
          this.playing = true;
        } else {
          this.player.pause();
          this.playing = false;
        }
      }
    },
    openSponsorLink() {
      this.$gtag.navigation('click_sponsor_link', this.videoAd.url);
      window.open(this.videoAd.url, '_blank');
      this.$emit('clicked');
    },
    onkeydown(e) {
      var key = e.keyCode;
      this.keys[key] = true;
      if (this.keys[65] && this.keys[68]) {
        this.loadBroadcast();
      }
    },
    onkeyup(e) {
      var key = e.keyCode;
      this.keys[key] = false;
    },
    playerMuted() {
      if (this.player) {
        return this.player.getMute();
      }
      return null;
    },
    playerPaused() {
      if (this.player) {
        let state = this.player.getState();
        switch (state) {
          case 'idle':
            return true;
          case 'playing':
            return false;
          case 'paused':
            return true;
        }
      }
      return null;
    },
    addKeyEventListeners() {
      document.onkeydown = this.onkeydown;
      document.onkeyup = this.onkeyup;
    },
    removeKeyEventListeners() {
      document.onkeydown = null;
      document.onkeyup = null;
    },
  },
  mounted() {
    this.loadPlayerScript();
  },
  destroyed() {
    this.removeKeyEventListeners();
  },
};
</script>
<style scoped>
.playerContainer {
  position: relative;
}
.playerContainer:not(.loaded) {
  padding-top: 56.25%;
  background-color: gray;
}
.loadingMedia {
  position: absolute;
  left: calc(50% - 50px);
  top: 50%;
}
.customControl {
  position: absolute;
  opacity: 0.6;
}
.customControl:hover {
  opacity: 0.9;
}
.skipAdButton {
  top: 10px;
  left: 10px;
}
.playButton {
  bottom: 10px;
  left: 10px;
}
.muteButton {
  bottom: 10px;
  left: 82px;
}
.skipAdText {
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 7px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 8px;
  color: #fff;
}
#kslPlayerContainer {
  position: relative;
  width: 0;
  height: 0;
  overflow: hidden;
}
#kslPlayerContainer.adPlayed {
  width: 100%;
  padding-top: 56.3%;
}
#kslPlayerContainer iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
